<template>
  <div class="face-diagnosis-page">
    <van-nav-bar
      title="面诊报告详情"
      left-arrow
      @click-left="onClickLeft"
      fixed
      placeholder
      safe-area-inset-top
    />

    <!-- Loading -->
    <div class="page_loading" v-if="!resultData && isLoading">
      <van-loading type="spinner" color="#44C2C4" vertical class="analyze-loading">正在获取报告...</van-loading>
    </div>

    <!-- Empty State -->
    <div v-else-if="!resultData && !isLoading" style="padding-top: 100px;">
      <van-empty description="暂无面诊报告">
        <van-button round type="info" color="#44C2C4" style="margin-top:20px;" @click="loadMockData">
          查看演示效果 (Mock 数据)
        </van-button>
      </van-empty>
    </div>

    <template v-if="resultData && resultData.faceReport">
      <van-tabs
        v-model="activeTab"
        class="page-tabs"
        color="#44C2C4"
        line-width="40px"
        title-active-color="#44C2C4"
      >
        
        <!-- =================== 表现症状 =================== -->
        <van-tab title="表现症状">
          <div class="report-content teal-bg">
            <div class="white-container">
              <!-- 1. 头部信息 -->
              <div class="top-info">
                <img class="head-img" :src="resultData.faceReport.headImg || defaultAvatar" />
                <div class="result-text">
                  <div class="result-label">检测结果：</div>
                  <div class="result-highlight">{{ resultData.faceReport.result || '正常' }}</div>
                </div>
                <div class="cause-text"><strong>形成原因：</strong>{{ resultData.faceReport.cause || '暂无' }}</div>
              </div>

              <!-- 2. 面诊解析 -->
              <div class="section-container" v-if="resultData.faceReport.facialAnalysis && resultData.faceReport.facialAnalysis.list">
                <div class="section-title-wrap">
                  <div class="title-left"><span class="title-dot"></span>面诊解析</div>
                  <div class="title-more" @click="goToFacialAnalysis">查看更多 <van-icon name="arrow" /></div>
                </div>
                <div class="custom-table">
                  <div class="table-header">
                    <div class="col-1">面像分析</div>
                    <div class="col-2">检测结果</div>
                    <div class="col-3">标准值</div>
                  </div>
                  <div class="table-row" v-for="(item, index) in resultData.faceReport.facialAnalysis.list" :key="index">
                    <div class="col-1">{{ item.name }}</div>
                    <div class="col-2" :class="{'warn-text': isWarn(item.result)}">{{ item.result }}</div>
                    <div class="col-3">{{ item.standard }}</div>
                  </div>
                </div>
              </div>

              <!-- 3. 皮肤分析 -->
              <div class="section-container" v-if="resultData.faceReport.skinAnalysis && resultData.faceReport.skinAnalysis.list">
                <div class="section-title-wrap">
                  <div class="title-left"><span class="title-dot"></span>皮肤分析</div>
                  <div class="title-more" @click="goToSkinAnalysis">查看更多 <van-icon name="arrow" /></div>
                </div>
                <div class="skin-grid">
                  <div class="skin-item" v-for="(item, index) in resultData.faceReport.skinAnalysis.list" :key="index">
                    <div class="skin-name">
                      <img
                        v-if="item.icon"
                        :src="item.icon"
                        :alt="item.name"
                        class="skin-icon"
                      />
                      <van-icon v-else name="photo-o" size="18" color="#D0A77A"/>
                      {{ item.name }}
                    </div>
                    <div :class="getLevelClass(item.level)">{{ item.level }}</div>
                  </div>
                </div>
              </div>

              <!-- 4. 健康分析 -->
              <div class="section-container">
                <div class="section-title-wrap">
                  <div class="title-left"><span class="title-dot"></span>健康分析</div>
                </div>
                <div class="health-list">
                  <!-- 易患病症 -->
                  <div class="health-card" @click="goToSusceptibleDiseases">
                    <div class="health-header">
                      <div class="health-title">
                        <img
                          src="https://pro-yeahgo-oss.yeahgo.com/file/zywzimg/易患病症.png"
                          alt="易患病症"
                          class="health-icon"
                        />
                        易患病症
                      </div>
                      <div class="health-more">查看 <van-icon name="arrow" /></div>
                    </div>
                    <div class="health-desc">AI面诊识别健康隐患，智能预警助疾病预防。</div>
                  </div>
                  <!-- 脏腑易患病症 -->
                  <div class="health-card" @click="goToOrganDiseases">
                    <div class="health-header">
                      <div class="health-title">
                        <img
                          src="https://pro-yeahgo-oss.yeahgo.com/file/zywzimg/肺腑易患病症.png"
                          alt="脏腑易患病症"
                          class="health-icon"
                        />
                        脏腑易患病症
                      </div>
                      <div class="health-more">查看 <van-icon name="arrow" /></div>
                    </div>
                    <div class="health-desc">AI面诊智能辨识脏腑隐患，提供智能健康预警。</div>
                  </div>
                  <!-- 经络隐患 -->
                  <div class="health-card" @click="goToMeridianRisks">
                    <div class="health-header">
                      <div class="health-title">
                        <img
                          src="https://pro-yeahgo-oss.yeahgo.com/file/zywzimg/经络隐患.png"
                          alt="经络隐患"
                          class="health-icon"
                        />
                        经络隐患
                      </div>
                      <div class="health-more">查看 <van-icon name="arrow" /></div>
                    </div>
                    <div class="health-desc">AI面诊智能分析经络隐患，提供日常保健指导。</div>
                  </div>
                </div>
              </div>

              <!-- 底部声明与时间 -->
              <div class="bottom-statement">
                <div class="statement-text">
                  重要声明：本报告采用AI人工智能分析总结，仅供参考，不可作为医疗诊断结论，如已有身体不适，请及时咨询医生。
                </div>
                <div class="time-text" v-if="resultData.createAt">
                  生成时间：{{ formatTime(resultData.createAt) }}
                </div>
              </div>

            </div>
          </div>
        </van-tab>

        <!-- =================== 调理建议 =================== -->
         <van-tab title="调理建议">
          <div class="report-content suggest-bg" v-if="resultData.faceReport.suggestions">
            <div class="page-header-center">
              <img src="https://pro-yeahgo-oss.yeahgo.com/file/zywzimg/原理说明.png" alt="调理建议" class="page-header-icon" />
              <span class="page-header-title">调理建议</span>
            </div>
            
            <!-- 顶部三个小 Tab 按钮，这里只做锚点/装饰，不再控制 v-show -->
            <div class="suggest-tabs" ref="suggestTabsContainer">
              <div 
                class="suggest-tab-item" 
                v-for="tab in suggestTabs" 
                :key="tab.id"
                :ref="`tab-${tab.id}`"
                :class="{ active: activeSubTab === tab.id }"
                @click="scrollToTab(tab.id)"
              >
                {{ tab.title }}
              </div>
            </div>

            <!-- 将所有内容放在一个连续的容器中 -->
            <div class="sub-tab-content">
              
              <!-- 【区块】 产品/服务搭配 -->
              <div class="suggest-block" id="suggest-product">
                <template v-if="faceGoodsList.length">
                  <div class="block-title">调理推荐</div>
                  <div class="goods-list">
                    <div
                      v-for="(item, index) in faceGoodsList"
                      :key="`${item.title || item.name}-${index}`"
                      class="promo-card"
                      @click="onGoodsClick(item)"
                    >
                      <img
                        class="promo-img"
                        :src="item.image || item.img || item.picUrl || item.url || defaultAvatar"
                        :alt="item.title || item.name || '商品图片'"
                      />
                      <div class="promo-body">
                        <div class="promo-title van-multi-ellipsis--l3">{{ item.title || item.name }}</div>
                        <div class="promo-footer">
                          <div class="promo-market-price">
                            市场价
                            <span>¥{{ formatPrice(item.marketPrice || item.market_price || 0) }}</span>
                          </div>
                          <div class="promo-price-row">
                            <div class="promo-sale-price">
                              <span class="price-symbol">¥</span>{{ formatPrice(item.salePrice || item.sale_price || item.price || 0) }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
                <van-empty v-else image="search" description="暂时没有商品，敬请期待！" />
              </div>

              <!-- 【区块】 中医调理 -->
              <div class="suggest-block" id="suggest-tcm" v-if="resultData.faceReport.suggestions.tCMRegulationSuggestions">
                <div class="block-title">
                  <img src="https://pro-yeahgo-oss.yeahgo.com/file/zywzimg/中医调理建议.png" alt="中医调理建议" class="title-icon" /> 中医调理建议
                </div>
                <div class="block-card">
                  {{ resultData.faceReport.suggestions.tCMRegulationSuggestions }}
                </div>
              </div>

              <!-- 【区块】 经络保健建议 -->
              <div class="suggest-block" id="suggest-meridian" v-if="resultData.faceReport.suggestions.meridianHealthCare && resultData.faceReport.suggestions.meridianHealthCare.list && resultData.faceReport.suggestions.meridianHealthCare.list.length">
                <div class="block-title">
                  <img src="https://pro-yeahgo-oss.yeahgo.com/file/zywzimg/经络保健建议.png" alt="经络保健建议" class="title-icon" /> 经络保健建议
                </div>
                <div class="block-card">
                  <div class="card-subtitle">经络建议：</div>
                  <div class="card-text" v-if="resultData.faceReport.suggestions.meridianHealthCare.suggestions">{{ resultData.faceReport.suggestions.meridianHealthCare.suggestions }}</div>
                  
                  <div class="meridian-divider"></div>
                  
                  <div class="card-subtitle" style="margin-top: 12px;">经络推荐：</div>
                  <div class="meridian-grid">
                    <div class="meridian-item" v-for="(item, idx) in resultData.faceReport.suggestions.meridianHealthCare.list" :key="idx">
                      <img :src="item.img || 'https://img01.yzcdn.cn/vant/ipad.jpeg'" />
                      <span>{{ item.name }}</span>
                    </div>
                  </div>
                  <div class="card-note">注：每天按照第一天的调理方案循环灸治痊愈为止</div>
                </div>
              </div>

              <!-- 【区块】 穴位保健 -->
              <div class="suggest-block" id="suggest-acupoint" v-if="resultData.faceReport.suggestions.acupointHealthCare && resultData.faceReport.suggestions.acupointHealthCare.list && resultData.faceReport.suggestions.acupointHealthCare.list.length">
                <div class="block-title">
                  <img src="https://pro-yeahgo-oss.yeahgo.com/file/zywzimg/穴位保健.png" alt="穴位保健" class="title-icon" /> 穴位保健
                </div>
                <div class="block-card">
                  <div class="card-subtitle">穴位保健建议</div>
                  <div class="card-text" v-if="resultData.faceReport.suggestions.acupointHealthCare.suggestions">{{ resultData.faceReport.suggestions.acupointHealthCare.suggestions }}</div>
                  <div class="card-text" v-if="resultData.faceReport.suggestions.acupointHealthCare.method"><strong>方法：</strong>{{ resultData.faceReport.suggestions.acupointHealthCare.method }}</div>
                  
                  <div class="meridian-divider"></div>
                  
                  <div class="acupoint-list">
                    <div class="acupoint-item" v-for="(item, idx) in resultData.faceReport.suggestions.acupointHealthCare.list" :key="idx">
                      <img :src="item.img || 'https://img01.yzcdn.cn/vant/ipad.jpeg'" />
                      <span>{{ item.name }}</span>
                    </div>
                  </div>
                  <div class="card-note">注：每天按照第一天的调理方案循环灸治痊愈为止</div>
                </div>
              </div>

              <!-- 【区块】 饮食调养建议 -->
              <div class="suggest-block" id="suggest-diet" v-if="resultData.faceReport.suggestions.foodsSuggestions">
                <div class="block-title">
                  <img src="https://pro-yeahgo-oss.yeahgo.com/file/zywzimg/饮食调养建议.png" alt="饮食调养建议" class="title-icon" /> 饮食调养建议
                </div>
                <div class="block-card" v-if="resultData.faceReport.suggestions.foodsSuggestions.suggestions">
                  <div class="card-subtitle">饮食建议：</div>
                  {{ resultData.faceReport.suggestions.foodsSuggestions.suggestions }}
                </div>
                <div class="block-card" style="margin-top: 12px;">
                  <div class="yi-ji-row" v-if="resultData.faceReport.suggestions.foodsSuggestions.recommend">
                    <div class="yi-icon">宜</div>
                    <div class="yi-ji-text">{{ resultData.faceReport.suggestions.foodsSuggestions.recommend }}</div>
                  </div>
                  <div class="yi-ji-row" v-if="resultData.faceReport.suggestions.foodsSuggestions.avoid" style="margin-top: 16px;">
                    <div class="ji-icon">忌</div>
                    <div class="yi-ji-text">{{ resultData.faceReport.suggestions.foodsSuggestions.avoid }}</div>
                  </div>
                </div>
              </div>

              <!-- 【区块】 推荐饮食 -->
              <div class="suggest-block" id="suggest-food" v-if="resultData.faceReport.suggestions.recommendFoods && resultData.faceReport.suggestions.recommendFoods.list && resultData.faceReport.suggestions.recommendFoods.list.length">
                <div class="food-section-title">推荐饮食</div>
                <div class="food-card" v-for="(item, idx) in resultData.faceReport.suggestions.recommendFoods.list" :key="idx">
                  <div class="food-card-header">
                    <img class="food-img" src="https://img01.yzcdn.cn/vant/cat.jpeg" />
                    <div class="food-name">{{ item.name }}</div>
                  </div>
                  <div class="food-detail">
                    <div v-if="item.ingredients"><strong>食材：</strong>{{ item.ingredients }}</div>
                    <div v-if="item.cookingMethods"><strong>烹饪方法：</strong>{{ item.cookingMethods }}</div>
                    <div v-if="item.efficacy"><strong>功效：</strong>{{ item.efficacy }}</div>
                  </div>
                </div>
              </div>

              <!-- 【区块】 运动保健 -->
              <div class="suggest-block" id="suggest-exercise" v-if="resultData.faceReport.suggestions.exerciseHealthCareSuggestions">
                <div class="block-title">
                  <img src="https://pro-yeahgo-oss.yeahgo.com/file/zywzimg/运动保健.png" alt="运动保健" class="title-icon" /> 运动保健
                </div>
                <div class="block-card">
                  <div class="card-subtitle">运动保健建议</div>
                  {{ resultData.faceReport.suggestions.exerciseHealthCareSuggestions }}
                </div>
              </div>

              <!-- 【区块】 情致调养 -->
              <div class="suggest-block" id="suggest-emotion" v-if="resultData.faceReport.suggestions.emotionalNourishmentSuggestions">
                <div class="block-title">
                  <img src="https://pro-yeahgo-oss.yeahgo.com/file/zywzimg/情致调养.png" alt="情致调养" class="title-icon" /> 情致调养
                </div>
                <div class="block-card">
                  {{ resultData.faceReport.suggestions.emotionalNourishmentSuggestions }}
                </div>
              </div>

              <!-- 【区块】 起居调养 -->
              <div class="suggest-block" id="suggest-living" v-if="resultData.faceReport.suggestions.dailyLivingNourishmentSuggestions">
                <div class="block-title">
                  <img src="https://pro-yeahgo-oss.yeahgo.com/file/zywzimg/起居调养.png" alt="起居调养" class="title-icon" /> 起居调养
                </div>
                <div class="block-card">
                  {{ resultData.faceReport.suggestions.dailyLivingNourishmentSuggestions }}
                </div>
              </div>

            </div>

            <!-- 底部声明与时间 -->
            <div class="bottom-statement bg-gray" style="margin-top: 24px;">
              <div class="statement-text">
                重要声明：本报告采用AI人工智能分析总结，仅供参考，不可作为医疗诊断结论，如已有身体不适，请及时咨询医生。
              </div>
              <div class="time-text" v-if="resultData.createAt">
                生成时间：{{ formatTime(resultData.createAt) }}
              </div>
            </div>

          </div>
        </van-tab>
      </van-tabs>
    </template>
  </div>
</template>

<script>
import { post } from '@/utils/request';
import { Loading, Tabs, Tab, Toast, Empty, Icon, Button, NavBar } from 'vant';

export default {
  name: 'MyAiFaceDiagnosis',
  components: {
    [Loading.name]: Loading,
    [Tabs.name]: Tabs,
    [Tab.name]: Tab,
    [Empty.name]: Empty,
    [Icon.name]: Icon,
    [Button.name]: Button,
    [NavBar.name]: NavBar,
  },
  data() {
    return {
      isLoading: true,
      resultData: null,
      activeTab: 0,
      activeSubTab: 'product',
      isClickScrolling: false,
      clickScrollTimer: null,
      scrollEndTimer: null,
      pendingActiveTabId: '',
      defaultAvatar: 'https://img01.yzcdn.cn/vant/cat.jpeg'
    };
  },
  computed: {
    faceGoodsList() {
      const list = this.resultData?.faceReport?.suggestions?.goodsList
        || this.resultData?.faceReport?.goodsList
        || this.resultData?.goodsList
        || [];
      if (!Array.isArray(list)) return [];

      return [...list].sort((a, b) => {
        const sortA = Number(a?.sort || 0);
        const sortB = Number(b?.sort || 0);
        return sortA - sortB;
      });
    },
    suggestTabs() {
      if (!this.resultData?.faceReport?.suggestions) return [];
      const s = this.resultData.faceReport.suggestions;
      const tabs = [];
      tabs.push({ id: 'product', title: '产品/服务搭配' });
      if (s.tCMRegulationSuggestions) tabs.push({ id: 'tcm', title: '中医调理' });
      if (s.meridianHealthCare?.list?.length) tabs.push({ id: 'meridian', title: '经络保健' });
      if (s.acupointHealthCare?.list?.length) tabs.push({ id: 'acupoint', title: '穴位保健' });
      if (s.foodsSuggestions) tabs.push({ id: 'diet', title: '饮食调养' });
      if (s.recommendFoods?.list?.length) tabs.push({ id: 'food', title: '推荐饮食' });
      if (s.exerciseHealthCareSuggestions) tabs.push({ id: 'exercise', title: '运动保健' });
      if (s.emotionalNourishmentSuggestions) tabs.push({ id: 'emotion', title: '情致调养' });
      if (s.dailyLivingNourishmentSuggestions) tabs.push({ id: 'living', title: '起居调养' });
      return tabs;
    }
  },
  created() {
    let reportId = this.$route.query.reportId;
    if (!reportId) {
      const payloadStr = sessionStorage.getItem("MY_AI_SUBMIT_PAYLOAD") || localStorage.getItem("MY_AI_SUBMIT_PAYLOAD");
      if (payloadStr) {
        try {
          const payload = JSON.parse(payloadStr);
          reportId = payload.reportId;
        } catch (e) {
          console.error("解析缓存数据失败", e);
        }
      }
    }

    if (reportId) {
      this.fetchReportData(reportId);
    } else {
      this.isLoading = false;
      Toast({ message: "缺少报告ID", position: 'middle' });
    }
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll, true);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll, true);
    if (this.clickScrollTimer) {
      clearTimeout(this.clickScrollTimer);
      this.clickScrollTimer = null;
    }
    if (this.scrollEndTimer) {
      clearTimeout(this.scrollEndTimer);
      this.scrollEndTimer = null;
    }
  },
  methods: {
    formatPrice(value) {
      const amount = Number(value || 0) / 100;
      return Number.isInteger(amount) ? amount : amount.toFixed(2);
    },
    onClickLeft() {
      this.$router.go(-1);
    },
    goToFacialAnalysis() {
      if (this.resultData && this.resultData.faceReport && this.resultData.faceReport.facialAnalysis) {
        const dataToPass = {
          headImg: this.resultData.faceReport.headImg,
          createAt: this.resultData.createAt,
          facialAnalysis: this.resultData.faceReport.facialAnalysis
        };
        sessionStorage.setItem("MY_AI_FACIAL_ANALYSIS", JSON.stringify(dataToPass));
      }
      this.goTo('/web/my-ai/facial-analysis');
    },
    goToSkinAnalysis() {
      if (this.resultData && this.resultData.faceReport && this.resultData.faceReport.skinAnalysis) {
        const dataToPass = {
          headImg: this.resultData.faceReport.headImg,
          createAt: this.resultData.createAt,
          skinAnalysis: this.resultData.faceReport.skinAnalysis
        };
        sessionStorage.setItem("MY_AI_SKIN_ANALYSIS", JSON.stringify(dataToPass));
      }
      this.goTo('/web/my-ai/skin-analysis');
    },
    goToSusceptibleDiseases() {
      if (this.resultData && this.resultData.faceReport && this.resultData.faceReport.predisposingDiseases) {
        sessionStorage.setItem("MY_AI_PREDISPOSING_DISEASES", JSON.stringify(this.resultData.faceReport.predisposingDiseases));
      }
      this.goTo('/web/my-ai/susceptible-diseases');
    },
    goToOrganDiseases() {
      if (this.resultData && this.resultData.faceReport && this.resultData.faceReport.zangFuOrganHiddenDiseases) {
        const dataToPass = {
          createAt: this.resultData.createAt,
          ...this.resultData.faceReport.zangFuOrganHiddenDiseases
        };
        sessionStorage.setItem("MY_AI_ORGAN_DISEASES", JSON.stringify(dataToPass));
      }
      this.goTo('/web/my-ai/organ-diseases');
    },
    goToMeridianRisks() {
      if (this.resultData && this.resultData.faceReport && this.resultData.faceReport.meridianHiddenTroubles) {
        const dataToPass = {
          createAt: this.resultData.createAt,
          ...this.resultData.faceReport.meridianHiddenTroubles
        };
        sessionStorage.setItem("MY_AI_MERIDIAN_RISKS", JSON.stringify(dataToPass));
      }
      this.goTo('/web/my-ai/meridian-risks');
    },
    fetchReportData(reportId) {
      post({
        url: "/healthy/auth/consultation/faceReport",
        data: { reportId },
      })
        .then((res) => {
          const result = res?.data || res || null;
          this.resultData = result;
          this.isLoading = false;
        })
        .catch(() => {
          this.isLoading = false;
          Toast({ message: "获取面诊报告失败", position: 'middle' });
        });
    },
    goTo(path) {
      this.$router.push({ path, query: this.$route.query });
    },
    isWarn(result) {
      if (!result) return false;
      return result !== '无' && result !== '正常';
    },
    getLevelClass(level) {
      if (!level) return '';
      if (level.includes('无') || level.includes('正常')) {
        return 'level-normal';
      }
      return 'level-warn';
    },
    getStickyOffset() {
      // 动态计算顶部三个吸附区域的总高度
      // nav-bar 约 46px，van-tabs 固定是 44px
      // suggest-tabs 根据实际渲染的 DOM 高度来获取，如果没有则预估 56px
      let suggestTabsHeight = 56;
      if (this.$refs.suggestTabsContainer) {
        suggestTabsHeight = this.$refs.suggestTabsContainer.offsetHeight;
      }
      return 46 + 44 + suggestTabsHeight;
    },
    getSectionTitleOffset(id) {
      const sectionEl = document.getElementById(`suggest-${id}`);
      if (!sectionEl) return 0;

      const titleEl = sectionEl.querySelector('.block-title, .food-section-title');
      if (!titleEl) return 0;

      const titleStyle = window.getComputedStyle(titleEl);
      const marginBottom = Number.parseFloat(titleStyle.marginBottom || '0') || 0;
      return titleEl.offsetHeight + marginBottom;
    },
    getSectionScrollOffset(id) {
      const titleOffset = this.getSectionTitleOffset(id);
      return this.getStickyOffset() + Math.max(titleOffset / 3, 0) + 6;
    },
    getCurrentSectionId(preferredId = '') {
      const tabs = this.suggestTabs;
      if (!tabs.length) return '';

      const metrics = tabs.map((tab) => {
        const el = document.getElementById(`suggest-${tab.id}`);
        if (!el) return null;

        const rectTop = el.getBoundingClientRect().top;
        return {
          id: tab.id,
          rectTop
        };
      }).filter(Boolean);

      if (!metrics.length) return tabs[0].id;

      const anchorLine = this.getStickyOffset() + 8;

      if (preferredId) {
        const preferredMetric = metrics.find(item => item.id === preferredId);
        if (preferredMetric && preferredMetric.rectTop <= anchorLine + 28) {
          return preferredId;
        }
      }

      const currentVisible = [...metrics].reverse().find(item => item.rectTop <= anchorLine);
      return currentVisible?.id || tabs[0].id;
    },
    scrollToTab(id) {
      this.activeSubTab = id;
      this.pendingActiveTabId = id;
      this.isClickScrolling = true;
      if (this.clickScrollTimer) {
        clearTimeout(this.clickScrollTimer);
      }
      if (this.scrollEndTimer) {
        clearTimeout(this.scrollEndTimer);
        this.scrollEndTimer = null;
      }
      const el = document.getElementById(`suggest-${id}`);
      if (el) {
        const offset = this.getSectionScrollOffset(id);
        const y = el.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top: y, behavior: 'smooth' });
        this.centerTab(id, false);
        this.clickScrollTimer = setTimeout(() => {
          this.clickScrollTimer = null;
        }, 1200);
      }
    },
    updateActiveTabByScroll() {
      const currentId = this.getCurrentSectionId(this.pendingActiveTabId);
      this.pendingActiveTabId = '';

      if (this.activeSubTab !== currentId) {
        this.activeSubTab = currentId;
        this.centerTab(currentId, false);
      }
    },
    onScroll() {
      if (this.activeTab !== 1) return; // 只在调理建议Tab下执行
      if (this.scrollEndTimer) {
        clearTimeout(this.scrollEndTimer);
      }

      if (this.isClickScrolling) {
        this.scrollEndTimer = setTimeout(() => {
          this.isClickScrolling = false;
          this.scrollEndTimer = null;
          this.updateActiveTabByScroll();
        }, 160);
        return;
      }

      this.scrollEndTimer = setTimeout(() => {
        this.scrollEndTimer = null;
        this.updateActiveTabByScroll();
      }, 160);
    },
    centerTab(id, smooth = false) {
      this.$nextTick(() => {
        const tabEl = this.$refs[`tab-${id}`]?.[0];
        const container = this.$refs.suggestTabsContainer;
        if (tabEl && container) {
          const tabLeft = tabEl.offsetLeft;
          const tabRight = tabLeft + tabEl.offsetWidth;
          const visibleLeft = container.scrollLeft;
          const visibleRight = visibleLeft + container.offsetWidth;

          if (tabLeft >= visibleLeft && tabRight <= visibleRight) {
            return;
          }

          const targetLeft = tabLeft - (container.offsetWidth / 2) + (tabEl.offsetWidth / 2);
          const maxScrollLeft = Math.max(0, container.scrollWidth - container.offsetWidth);
          const nextScrollLeft = Math.min(Math.max(targetLeft, 0), maxScrollLeft);

          container.scrollTo({ left: nextScrollLeft, behavior: smooth ? 'smooth' : 'auto' });
        }
      });
    },
    onGoodsClick(item) {
      const actionUrl = item.actionUrl || item.action_url || '';
      if (item.wxAppId) {
        if (window.wx && window.wx.miniProgram) {
          window.wx.miniProgram.navigateTo({
            url: actionUrl
          });
        } else {
          Toast('请在微信小程序中打开');
        }
      } else if (actionUrl.startsWith('http')) {
        window.location.href = actionUrl;
      } else if (actionUrl) {
        this.$router.push(actionUrl);
      }
    },
    formatTime(timeStr) {
      if (!timeStr) return '';
      // 将 "2026-03-30 18:02:30" 转换为 "2026年3月30日 18:02:30"
      try {
        const parts = timeStr.split(' ');
        const dateParts = parts[0].split('-');
        return `${dateParts[0]}年${parseInt(dateParts[1], 10)}月${parseInt(dateParts[2], 10)}日 ${parts[1] || ''}`;
      } catch (e) {
        return timeStr;
      }
    },
    loadMockData() {
      this.resultData = {
        createAt: "2026-04-03 18:28:37",
        faceReport: {
          headImg: "https://img01.yzcdn.cn/vant/ipad.jpeg",
          result: "正常",
          cause: "按五行理论，土形人面色稍黄或长夏面色偏黄为正常。",
          facialAnalysis: {
            list: [
              { name: "五色诊", result: "黄色", standard: "面色红黄隐隐，明润含蓄" },
              { name: "光泽", result: "正常", standard: "正常" },
              { name: "两颧红", result: "无", standard: "无" },
              { name: "黑眼圈", result: "轻度", standard: "正常" },
              { name: "眼袋", result: "轻度", standard: "正常" },
              { name: "面部皮损", result: "痘痘", standard: "无" }
            ]
          },
          skinAnalysis: {
            list: [
              { name: "黑眼圈", level: "轻度" },
              { name: "痘痘", level: "轻度" },
              { name: "色斑", level: "无" },
              { name: "皱纹", level: "轻度" },
              { name: "粗糙度", level: "轻度" },
              { name: "黑头", level: "无" },
              { name: "毛孔", level: "无" },
              { name: "水份", level: "无" }
            ]
          },
          suggestions: {
            tCMRegulationSuggestions: "顺应四时变化，起居有常...",
            exerciseHealthCareSuggestions: "建议太极拳、八段锦等...",
            foodsSuggestions: {
              suggestions: "日常饮食均衡搭配",
              recommend: "苹果、橘子",
              avoid: "生冷辛辣"
            },
            recommendFoods: {
              list: [
                { name: "山药炖排骨", ingredients: "山药，排骨", cookingMethods: "慢火炖煮", efficacy: "健脾益气" }
              ]
            }
          }
        }
      };
    }
  }
};
</script>

<style lang="scss" scoped>
.face-diagnosis-page {
  background: #fff;
  min-height: 100vh;

  ::v-deep .van-nav-bar--fixed {
    z-index: 1001;
  }

  ::v-deep .page-tabs > .van-tabs__wrap {
    position: fixed;
    top: 46px;
    left: 0;
    right: 0;
    background: #fff;
    z-index: 1000;
  }
}

.page_loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 80vh;
  ::v-deep .van-loading__spinner { width: 36px; height: 36px; }
  ::v-deep .van-loading__text { font-size: 16px; }
}

.report-content {
  padding-top: 44px;
  padding-bottom: 30px;
}

.teal-bg {
  background: #44C2C4;
  padding: 16px 12px;
}

.white-container {
  background: #fff;
  border-radius: 12px;
  padding: 20px 0;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
}
.suggest-bg {
  background: #F7F8FA; /* 调理建议部分通常背景会有区分 */
  min-height: 100vh;
}

/* 1. 头部信息 */
.top-info {
  text-align: center;
  padding: 24px 16px;
}
.head-img {
  width: 100px;
  height: 100px;
  border-radius: 12px;
  object-fit: cover;
  margin: 0 auto 16px;
  display: block;
}
.result-text {
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 15px;
  color: #333;
  margin-bottom: 16px;
}
.result-label {
  line-height: 22px;
  margin-bottom: 6px;
}
.result-highlight {
  font-size: 20px;
  font-weight: bold;
  line-height: 28px;
}
.cause-text {
  font-size: 14px;
  color: #666;
  text-align: left;
  line-height: 1.6;
}

/* 模块通用标题 */
.section-container {
  margin-bottom: 24px;
}
.section-title-wrap {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 16px;
  margin-bottom: 12px;
}
.title-left {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: bold;
  color: #333;
}
.title-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #D0A77A;
  margin-right: 8px;
}
.title-more {
  font-size: 13px;
  color: #999;
  display: flex;
  align-items: center;
}

/* 2. 面诊解析表格 */
.custom-table {
  margin: 0 16px;
  border-radius: 8px;
  overflow: hidden;
}
.table-header {
  display: flex;
  background: #44C2C4;
  color: #fff;
  padding: 10px 0;
  font-size: 13px;
  text-align: center;
  font-weight: 500;
}
.table-header .col-1,
.table-header .col-2,
.table-header .col-3 {
  color: #fff;
}
.table-row {
  display: flex;
  align-items: center;
  padding: 12px 0;
  font-size: 13px;
  text-align: center;
  color: #333;
}
.table-row:nth-child(even) {
  background: #fff;
}
.table-row:nth-child(odd) {
  background: #F7F8FA;
}
.col-1 { flex: 1; font-weight: 500;}
.col-2 { flex: 1; }
.col-3 { flex: 1.5; font-size: 12px; color: #666; padding: 0 4px;}
.warn-text { color: #FF6B00; font-weight: bold; }

/* 3. 皮肤分析网格 */
.skin-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding: 0 16px;
}
.skin-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #F7F8FA;
  padding: 14px 16px;
  border-radius: 8px;
}
.skin-name {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #333;
  gap: 8px;
  font-weight: 500;
}
.skin-icon {
  width: 36px;
  height: 36px;
  object-fit: contain;
  flex-shrink: 0;
}
.level-normal { color: #44C2C4; font-size: 13px; font-weight: 500;}
.level-warn { color: #85C46C; font-size: 13px; font-weight: 500;}

/* 4. 健康分析列表 */
.health-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0 16px;
}
.health-card {
  background: #F7F8FA;
  border-radius: 8px;
  padding: 12px 16px 12px 8px;
}
.health-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0;
}
.health-title {
  font-size: 15px;
  font-weight: bold;
  color: #333;
  display: flex;
  align-items: center;
  gap: 4px;
}
.health-icon {
  width: 52px;
  height: 52px;
  object-fit: contain;
  flex-shrink: 0;
}
.health-more {
  font-size: 13px;
  color: #999;
  display: flex;
  align-items: center;
}
.health-desc {
  font-size: 13px;
  color: #999;
  line-height: 1.5;
}

/* ================= 调理建议通用样式 ================= */
.suggest-bg {
  background: #F7F8FA;
  min-height: 100vh;
  padding-bottom: 20px;
}

.page-header-center {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 16px 8px;
}

.page-header-icon {
  width: 30px;
  height: 30px;
  margin-right: 8px;
  object-fit: contain;
}

.page-header-title {
  font-size: 18px;
  font-weight: bold;
  color: #333;
}

/* 顶部三个按钮 */
.suggest-tabs {
  display: flex;
  background: #fff;
  padding: 12px 16px;
  gap: 12px;
  overflow-x: auto;
  white-space: nowrap;
  position: sticky;
  top: 90px; /* nav-bar 46px + van-tabs 44px */
  z-index: 99;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none;  /* IE and Edge */
}
.suggest-tabs::-webkit-scrollbar {
  display: none;
}
.suggest-tab-item {
  flex: none;
  text-align: center;
  font-size: 14px;
  color: #333;
  border: 1px solid #333;
  background: transparent;
  border-radius: 20px;
  padding: 6px 16px;
  transition: all 0.3s;
}
.suggest-tab-item.active {
  background: #44C2C4;
  color: #fff;
  border-color: #44C2C4;
}

.sub-tab-content {
  padding: 16px;
}

/* 调理建议每个区块 */
.suggest-block {
  margin-bottom: 20px;
  scroll-margin-top: 102px;
}
.block-title {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: bold;
  color: #333;
  margin-bottom: 12px;
  justify-content: center;
}
.title-icon {
  width: 40px;
  height: 40px;
  object-fit: contain;
  margin-right: 1px;
}
.block-card {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  font-size: 14px;
  color: #333;
  line-height: 1.6;
  border: 1px solid #eaeaea;
}
.goods-list {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.promo-card {
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  gap: 10px;
  cursor: pointer;
}
.promo-img {
  width: 150px;
  height: 150px;
  flex-shrink: 0;
  border-radius: 6px;
  object-fit: cover;
}
.promo-body {
  flex: 1;
  position: relative;
  min-width: 0;
  padding: 4px 10px 6px 0;
}
.promo-title {
  color: #333;
  font-size: 15px;
  line-height: 1.5;
}
.promo-footer {
  position: absolute;
  left: 0;
  right: 10px;
  bottom: 6px;
}
.promo-market-price {
  color: #939293;
  margin-bottom: 8px;
  font-size: 12px;
}
.promo-market-price span {
  text-decoration: line-through;
}
.promo-price-row {
  display: flex;
  justify-content: flex-start;
  align-items: center;
}
.promo-sale-price {
  color: #FF000B;
  font-weight: bold;
  font-size: 20px;
}
.price-symbol {
  font-size: 12px;
}
.card-subtitle {
  font-weight: bold;
  margin-bottom: 8px;
}
.card-text {
  margin-bottom: 8px;
}
.card-note {
  font-size: 12px;
  color: #999;
  margin-top: 16px;
}

/* 饮食调养 宜忌 */
.yi-ji-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.yi-icon, .ji-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  color: #fff;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.yi-icon { background: #44C2C4; }
.ji-icon { background: #E65C5C; }
.yi-ji-text {
  flex: 1;
  font-size: 14px;
  color: #333;
  line-height: 1.5;
}

/* 经络/穴位列表 */
.meridian-divider {
  height: 1px;
  background: #eee;
  margin: 16px 0;
}
.meridian-grid {
  display: flex;
  gap: 16px;
  margin-top: 12px;
}
.meridian-item {
  flex: 1;
  text-align: center;
}
.meridian-item img {
  width: 100%;
  aspect-ratio: 3/4;
  object-fit: contain;
  border: 1px solid #eee;
  border-radius: 8px;
  margin-bottom: 8px;
  background: #fff;
}
.meridian-item span {
  font-size: 13px;
  color: #333;
}

.acupoint-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.acupoint-item {
  display: flex;
  align-items: center;
  gap: 16px;
}
.acupoint-item img {
  width: 120px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #eee;
}
.acupoint-item span {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

/* 推荐饮食 */
.food-section-title {
  font-size: 16px;
  font-weight: bold;
  color: #333;
  margin-bottom: 12px;
}
.food-card {
  background: #fff;
  border: 1px solid #eaeaea;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
}
.food-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}
.food-img {
  width: 50px;
  height: 50px;
  border-radius: 8px;
  object-fit: cover;
}
.food-name {
  font-size: 15px;
  font-weight: bold;
  color: #333;
}
.food-detail {
  font-size: 13px;
  color: #666;
  line-height: 1.6;
}
.food-detail div {
  margin-bottom: 6px;
}
.food-detail strong {
  color: #333;
}

/* 底部声明 */
.bottom-statement {
  padding: 24px 16px 80vh 16px; /* 底部增加大量留白 (80vh)，保证最后一个标签可以滚动到顶部 */
  background: #fff;
}
.bottom-statement.bg-gray {
  background: #F7F8FA;
}
.statement-text {
  font-size: 13px;
  color: #999;
  line-height: 1.6;
  margin-bottom: 8px;
}
.time-text {
  font-size: 13px;
  color: #999;
}
</style>